<template>
  <div class="flex flex-col h-full">

    <layout-page-head-section>
      <div class="flex gap-2 xs:gap-4 sm:items-start">

        <div class="flex gap-4 flex-1 min-w-0" v-auto-animate="{duration: 100}" :class="isCondensedHeader ? 'items-center' : null">

          <template v-if="!isCondensedHeader">
            <client-only>
              <div class="flex shrink-0" @click="() => isEntityOwner ? contextActions.editCover.click() : null" :role="isEntityOwner ? 'button' : null" :title="isEntityOwner ? contextActions.editCover.label : null">
                <tag-avatar :tag="tag" :size="avatarSize" />
              </div>
            </client-only>

            <div class="min-w-0 flex-1">
              <div class="flex min-w-0 flex-col justify-center">
                <tag-rename-merge size="xl" :tag="tag" @merged="onTagMerge" :disabled="!isEntityOwner">
                  <layout-page-heading>{{tag.name}}</layout-page-heading>
                </tag-rename-merge>
                <user-owner-badge v-if="!isEntityOwner" :user="tag.user" class="mt-0.5" />

              </div>
            </div>
          </template>

          <template v-else>
            <tag-avatar :tag="tag" size="sm" class="shrink-0"/>
            <p class="min-w-0 flex-1 break-long-string font-serif text-2xl">{{tag.name}}</p>
          </template>

        </div>

        <div class="gap-2 flex shrink-0 sm:hidden justify-end items-start">
          <u-dropdown :items="mobileOverflowMenu">
            <u-button
              :icon="COMMON_ICONS.moreHorizontal"
              variant="outline"
              color="charcoal"
            />
          </u-dropdown>
        </div>

        <div class="gap-2 hidden shrink-0 sm:flex justify-end">
          <template v-if="isEntityOwner">
            <u-button-group size="sm">
              <u-tooltip :text="contextActions.print.label">
                <u-button
                  :icon="contextActions.print.icon"
                  variant="outline"
                  color="charcoal"
                  @click="contextActions.print.click"
                />
              </u-tooltip>
              <u-tooltip :text="contextActions.share.label">
                <u-button
                  :icon="contextActions.share.icon"
                  variant="outline"
                  color="charcoal"
                  @click="contextActions.share.click"
                />
              </u-tooltip>

              <u-dropdown :items="desktopOwnerOverflowMenu">
                <u-button
                  :icon="COMMON_ICONS.moreHorizontal"
                  variant="outline"
                  color="charcoal"
                />
              </u-dropdown>
            </u-button-group>

            <u-dropdown :items="addFilesDropdownItems">
              <u-button size="sm" :icon="COMMON_ICONS.add">Add Files</u-button>
            </u-dropdown>
          </template>

          <template v-else>
            <u-button-group size="sm">
              <u-tooltip :text="contextActions.viewSlideshow.label">
                <u-button
                  :icon="contextActions.viewSlideshow.icon"
                  variant="outline"
                  color="charcoal"
                  @click="contextActions.viewSlideshow.click"
                />
              </u-tooltip>
              <u-tooltip :text="contextActions.download.label">
                <u-button
                  :icon="contextActions.download.icon"
                  variant="outline"
                  color="charcoal"
                  @click="contextActions.download.click"
                />
              </u-tooltip>
            </u-button-group>
          </template>

        </div>
      </div>

    </layout-page-head-section>

    <div class="flex-1 -mr-[9px] lg:-mr-[29px] overflow-hidden">
      <ClientOnly>
        <file-list
          center-empty-state
          :context="tag"
          :context-type="FILE_CONTEXTS.tag"
          :share-token="shareToken"
          :user-id="userId"
          :enable-local-file-drop-upload="canDropUpload"
          :upload-modal-props="dropUploadModalProps"
          :disable-selection="!isAuthenticated"
          loading-text="Loading Files"
          @list-item-click="goToFile"
        >
          <template #empty-state>
            <core-empty-state
              v-if="isEntityOwner"
              heading="Add Files to This Tag"
              description="Ready to find what you're looking for? Tag your files and say goodbye to the search struggle."
              :icon="COMMON_ICONS.tag"
            >
              <p class="text-center mt-8">
                <u-dropdown :items="addFilesDropdownItems">
                  <u-button size="xl">Start Adding Files</u-button>
                </u-dropdown>
              </p>
            </core-empty-state>
            <core-empty-state
              v-else
              heading="This Tag is Empty"
              description="No files have been added to this tag yet."
              :icon="COMMON_ICONS.album"
            />
          </template>
        </file-list>
      </ClientOnly>
      <file-list
        center-empty-state
        :context="tag"
        :context-type="FILE_CONTEXTS.tag"
        :share-token="shareToken"
        :user-id="userId"
        :enable-local-file-drop-upload="canDropUpload"
        :upload-modal-props="dropUploadModalProps"
        :disable-selection="!isAuthenticated"
        loading-text="Loading Files"
        @list-item-click="goToFile"
      >
        <template #empty-state>
          <core-empty-state
            v-if="isEntityOwner"
            heading="Add Files to This Tag"
            description="Ready to find what you're looking for? Tag your files and say goodbye to the search struggle."
            :icon="COMMON_ICONS.tag"
          >
            <p class="text-center mt-8">
              <u-dropdown :items="addFilesDropdownItems">
                <u-button size="xl">Start Adding Files</u-button>
              </u-dropdown>
            </p>
          </core-empty-state>
          <core-empty-state
            v-else
            heading="This Tag is Empty"
            description="No files have been added to this tag yet."
            :icon="COMMON_ICONS.album"
          />
        </template>
      </file-list>
    </div>

    <file-selection-toolbar :actions="fileSelectionActions" :context="tag" :context-type="FILE_CONTEXTS.tag" :user-id="userId" v-if="isAuthenticated" />

    <nuxt-page :route="route" page-key="tag-file-detail" />
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';

  const route = useRoute();
  const {tag_id_or_slug: tagId, user_id_or_slug: userId, share_token: shareToken} = route.params;
  const tagsStore = useTagsStore();
  const {tag} = storeToRefs(tagsStore);
  const {isAuthenticated} = useSessionStore();
  const isEntityOwner = computed(() => tag.value?.isOwnedByUser);
  const canDropUpload = computed(() => isAuthenticated && isEntityOwner.value);
  const dropUploadModalProps = computed(() => ({
    tag: tag.value,
    userId,
    shareToken,
    contextType: FILE_CONTEXTS.tag,
    contextId: tag.value?.id
  }));

  const {sm} = useScreenSize();
  const avatarSize = computed(() => sm.value ? '3xl' : '2xl');

  const isCondensedHeader = ref(false);
  useEventBus(FILE_LIST_SCROLL_THRESHOLD_EVENT).on(status => {
    isCondensedHeader.value = !!status;
  })

  const {refresh, pending, error} = await useAsyncData(
    `tag-details-${tagId}`,
    () => tagsStore.getTag({id: tagId, userId, shareToken})
  );

  if (error.value) {
    throw createError({
      ...error.value
    });
  }

  useForeverHead({
    title: computed(() => `${tag.value.name} | Tag by ${tag.value.user.name}`),
    socialImage: tag.value.avatarImage
  });

  function onTagMerge({tag}) {
    navigateTo(useBuildRoute().toTag({tag}), {replace: true});
  }

  function goToFile({item: file}) {
    navigateTo(`${route.path.replace(/\/$/, '')}/files/${file.id}${buildQueryString(route.query)}`);
  }


  //file selection actions
  const allFileSelectionActions = useFileActions().selectionToolbarActions;
  const fileSelectionActions = computed(() => {
    if (isEntityOwner.value) {
      return [
        allFileSelectionActions.organize,
        allFileSelectionActions.share,
        allFileSelectionActions.download,
        allFileSelectionActions.makeCover,
        allFileSelectionActions.removeFromContainer,
        allFileSelectionActions.merge
      ];
    }

    if (isAuthenticated) {
      return [
        allFileSelectionActions.download,
        allFileSelectionActions.copyToMyAccount
      ];
    }
  });


  //context actions
  const {getContextActions} = useTagActions();
  const contextActions = getContextActions({
    tag: tag.value,
    userId,
    shareToken
  });

  const addFilesDropdownItems = [[
    contextActions.upload,
    contextActions.addFiles
  ], [
    contextActions.digitize
  ]];

  const desktopOwnerOverflowMenu = [
    [
      contextActions.edit,
      contextActions.editCover,
      contextActions.declutter,
      contextActions.viewSlideshow,
      contextActions.download
    ],
    [contextActions.delete]
  ];

  const mobileOverflowMenu = computed(() => {
    if (isEntityOwner.value) {
      return [
        [
          contextActions.upload,
          contextActions.addFiles,
          contextActions.digitize,
        ],
        [
          contextActions.edit,
          contextActions.editCover,
          contextActions.share,
          contextActions.print,
          contextActions.declutter,
          contextActions.viewSlideshow,
          contextActions.download
        ],
        [contextActions.delete]
      ];
    }

    return [[
      contextActions.viewSlideshow,
      contextActions.download
    ]];

  });


  //tags panel
  const {open: openTags, close: closeTags} = useTagsNav();
  onMounted(() => {
    if (!shareToken) {
      openTags();
    }
  });

  onBeforeRouteLeave(() => {
    closeTags();
  });


  //set up the signup cookie which allows us to link users to the sharer during signup.
  onMounted(async () => {
    if (!isAuthenticated && shareToken) {
      await useCommonModals().openSignupFromShareModal({
        referrer: tag.value.user,
        referralSource: 'share-tag',
        referralContext: 'share'
      });
    }
  });
</script>
